import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Divider, } from '@material-ui/core';
import NumberInput from '../../../common/components/UI/Input/Number';
import ReactMultipleSelect from "../../../common/components/UI/Select/MultipleReactSelect";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EXCLUDED_HYPERFIND_IDS } from '../../constants';

/**
 * @typedef {{ value: number, ratioValue: number, label: string, ratioPercentageValue: number }} ISelectedHyperfindRatio
 * @typedef {{ value: number, label: string, desc: string, visibility: * }} IHyperfind
 * @param {{
 *  selectedHyperfindRatios: ISelectedHyperfindRatio[],
 *  setSelectedHyperfindRatios: (ratio: ISelectedHyperfindRatio[]) => void,
 *  hyperFindList: IHyperfind[],
 *  onChangeCallback: Function,
 *  setMaximumVtoCount: Function,
 * }} hyperfindRatioProps 
 */
const hyperfindRatio = ({selectedHyperfindRatios, setSelectedHyperfindRatios, hyperFindList, onChangeCallback, setErrors, setMaximumVtoCount}) => {
    const { t } = useTranslation('employmentBillboard');
    const countAll = selectedHyperfindRatios.reduce((a, b) => { return a + b.ratioValue }, 0)
    const [maxAwarding, setMaxAwarding] = useState(countAll);
    const [currentSum, setCurrentSum] = useState(countAll);

    /**
     * 
     * @param {number} value 
     */
    const handleMaxAwarding = (value) => {
        if (value > 10000) {
            value = 10000
        } else if (value < 1) {
            value = 1
        }
        setMaxAwarding(value);
        setMaximumVtoCount(value)

        onChangeCallback();
    }

    /**
     * 
     * @param {string} target 
     * @param {ISelectedHyperfindRatio[]} value 
     */
    const handleHyperfindChange = (target, value) => {
        setSelectedHyperfindRatios(value);

        const sum = value.map(ratio => ratio.ratioValue).reduce((a, b) => a + b, 0)
        setCurrentSum(sum);
        onChangeCallback();
    }

    /**
     * 
     * @param {number} value 
     * @param {number} target 
     */
    const handlePercentageRatio = (value, target) => {
        let sum = 0
        const list = selectedHyperfindRatios.map((item) => {
            if (item.value === target) {
                item.ratioPercentageValue = value
                item.ratioValue = Math.floor(value / 100 * maxAwarding)
                sum += item.ratioValue
                return item;
            } else {
                sum += item.ratioValue
                return item;
            }
        });
        setSelectedHyperfindRatios(list);
        setCurrentSum(sum);
        onChangeCallback()
    }

    /**
     * 
     * @param {number} value 
     * @param {number} target 
     */
    const handleRatio = (value, target) => {
        let sum = 0
        const list = selectedHyperfindRatios.map((item) => {
            if (item.value === target) {
                item.ratioValue = value
                item.ratioPercentageValue = Math.floor(value / maxAwarding * 100)
                sum += item.ratioValue
                return item;
            } else {
                sum += item.ratioValue
                return item;
            }
            });
        setSelectedHyperfindRatios(list);
        setCurrentSum(sum);
        onChangeCallback()
    }

    /**
     * 
     * @param {number} value 
     */
    const removeHyperfindRatio = (value) => {
        const filteredList = selectedHyperfindRatios.filter(ratio => ratio.value !== value);
        const sum = filteredList.map(ratio => ratio.ratioValue).reduce((a, b) => a + b, 0);
        
        setSelectedHyperfindRatios(filteredList);
        setCurrentSum(sum);

        onChangeCallback();
    }

    useEffect(() => {
        const updatedRatios = selectedHyperfindRatios.map(x => ({ ...x, ratioPercentageValue: Math.floor(x.ratioValue / maxAwarding * 100) }));
        setSelectedHyperfindRatios(updatedRatios);
    }, [maxAwarding]);


    useEffect(() => {
        setErrors(currentSum === 0 || maxAwarding !== currentSum);
    }, [maxAwarding, currentSum])

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <NumberInput
                    handleChangeInput={handleMaxAwarding}
                    label={t('form.maxSignupsAllowed')}
                    min={1}
                    max={10000}
                    step={1}
                    precision={0}
                    value={maxAwarding}
                    defaultValue={1}
                />
            </Grid>
            <Grid item xs={12}>
                <div style={{ fontSize: 12, color: '#989898', marginBottom: 5 }}>{t('form.signupsRatio')}</div>
                <ReactMultipleSelect
                    labelText={t('form.peoplePlaceholder')}
                    value={selectedHyperfindRatios}
                    target={'selectedHyperfindRatios'}
                    onChange={handleHyperfindChange}
                    optionsList={hyperFindList.filter(x => !EXCLUDED_HYPERFIND_IDS.includes(x.value))}
                    renderMultipleValues={false}
                    note={t('form.peopleDisclaimer')}
                />
            </Grid>
            {selectedHyperfindRatios.map(ratio => {
                return <Grid container item xs={12} alignItems={'center'}>
                    <Grid item xs={5}>
                        {ratio.label}
                    </Grid>
                    <Grid item container xs={3} alignItems={'center'}>
                        <Grid item xs={1}>
                            <span>{`% `}</span>
                        </Grid>
                        <Grid item xs={11}>
                            <NumberInput
                                handleChangeInput={handlePercentageRatio}
                                min={0}
                                max={100}
                                step={1}
                                precision={0}
                                value={ratio.ratioPercentageValue}
                                defaultValue={0}
                                target={ratio.value}
                                error={currentSum > maxAwarding || !!selectedHyperfindRatios.find(x => !x.label && !x.value)}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={3} justifyContent={'center'} alignItems={'center'} container>
                        <Grid item xs={11}>
                            <NumberInput
                                handleChangeInput={handleRatio}
                                min={0}
                                max={maxAwarding}
                                step={1}
                                precision={0}
                                value={ratio.ratioValue}
                                defaultValue={0}
                                target={ratio.value}
                                error={currentSum > maxAwarding}
                            />
                        </Grid>
                        <Grid item xs={1}>
                            <span>{` Unit(s)`}</span>
                        </Grid>
                    </Grid>
                    <Grid item xs={1}>
                        <div style={{transform: "scale(0.68)", float: "right", cursor: "pointer"}} onClick={() => removeHyperfindRatio(ratio.value)}>
                            <FontAwesomeIcon color="#BABABA" icon={['fal', 'times']} size="2x" />
                        </div>
                    </Grid>
                </Grid>
            })}
            <Grid item xs={12}>
                <Divider />
            </Grid>
            <Grid item xs={5}>Total :</Grid>
            <Grid item xs={3} justifyContent={'center'}><span>{`% ${Math.floor(currentSum / maxAwarding * 100) || 0} `}</span></Grid>
            <Grid item xs={3} justifyContent={'center'}><span>{currentSum} Unit(s)</span></Grid>
            <Grid item xs={1}></Grid>
        </Grid>
    )
}

export default hyperfindRatio;